import {
  ActionConfirmationModal,
  Button,
  SheetFooter,
  Typography,
} from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { useUser } from "@/components/UserProvider";
import { FormLoadingOverlay } from "@/components/ui/Form/FormLoadingOverlay";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useFileEditor } from "@/lib/data/react-query/files/use-file-editor";
import { fileOperationMessage } from "@/lib/data/requests/files/canonical-files";
import { VITE_APRYSE_LICENSE_KEY } from "@/lib/data/static/importEnv";
import { SLIDEOVER_CONFIRM_CLOSE_DESCRIPTION } from "@/lib/data/static/slideoverStatics";
import type { FirmFile } from "@/lib/types";
import { formatUsersName } from "@/lib/utils/format";
import { getFirmFileDisplayName } from "@/lib/utils/firmFile";
import {
  PDF_MIME_TYPE,
  PdfEditor,
  type PdfEditorHandle,
} from "@/components/features/matter/file-repo/PdfEditor";
import { XIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";

interface EditPdfFirmFileModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  sourceFile: FirmFile;
  onSaved?: () => void;
}

export const EditPdfFirmFileModal = ({
  open,
  setOpen,
  sourceFile,
  onSaved,
}: EditPdfFirmFileModalProps) => {
  const { currentUser } = useUser();
  const annotationUserName = formatUsersName(currentUser)?.trim();
  const editorFile = useFileEditor(sourceFile.id, open);
  const editorRef = useRef<PdfEditorHandle | null>(null);
  const [isEditorReady, setIsEditorReady] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [hasAppliedRedactions, setHasAppliedRedactions] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [saveMode, setSaveMode] = useState<"copy" | "original" | null>(null);
  const displayName = useMemo(
    () => getFirmFileDisplayName(sourceFile),
    [sourceFile],
  );

  useEffect(() => {
    if (!open) {
      setIsEditorReady(false);
      setIsExporting(false);
      setHasAppliedRedactions(false);
      setConfirmClose(false);
      setSaveMode(null);
      return;
    }
  }, [displayName, open]);

  const handleCreateFromPdf = async () => {
    if (!editorRef.current) {
      toast.error("PDF editor is not ready yet.");
      return;
    }

    setSaveMode("copy");
    setIsExporting(true);
    try {
      const blob = await editorRef.current.exportDocument();
      if (!blob) {
        toast.error("Unable to export the document.");
        return;
      }

      await editorFile.copy.mutateAsync({
        blob,
        contentType: PDF_MIME_TYPE,
        sourceName: displayName,
      });

      toast.success("Document saved as a copy.");
      onSaved?.();
      setOpen(false);
    } catch (error) {
      console.error("Failed to save edited PDF", error);
      toast.error(fileOperationMessage(error, "Failed to save document"));
    } finally {
      setIsExporting(false);
      setSaveMode(null);
    }
  };

  const handleSaveOriginal = async () => {
    if (!editorRef.current) {
      toast.error("PDF editor is not ready yet.");
      return;
    }

    setSaveMode("original");
    setIsExporting(true);

    try {
      const blob = await editorRef.current.exportDocument();
      if (!blob) {
        toast.error("Unable to export the document.");
        return;
      }

      await editorFile.save.mutateAsync({ blob, contentType: PDF_MIME_TYPE });

      toast.success("Document saved");
      onSaved?.();
      setOpen(false);
    } catch (error) {
      console.error("Failed to save PDF document", error);
      toast.error(fileOperationMessage(error, "Failed to save document"));
    } finally {
      setIsExporting(false);
      setSaveMode(null);
    }
  };

  const isSaving =
    editorFile.copy.isPending ||
    editorFile.save.isPending ||
    isExporting ||
    saveMode !== null;

  return (
    <AppModal
      open={open}
      onOpenChange={setOpen}
      isDirty
      hideHeader
      hideCloseButton
      title={`Edit ${displayName}`}
      description="Edit the PDF or mark redactions, then save a new version or create a separate copy."
      bodyContained
      className="top-0 left-0 h-[100dvh] !max-h-[100dvh] !w-screen !max-w-none translate-x-0 translate-y-0 rounded-none"
    >
      <div className="relative flex min-h-0 grow flex-col overflow-hidden">
        {isSaving && <FormLoadingOverlay />}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="absolute top-4 right-4 z-10 h-8 w-8 p-0"
          onClick={() => {
            setConfirmClose(true);
          }}
        >
          <XIcon className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </Button>
        <div className="flex min-h-0 flex-1 flex-col px-4 py-4">
          {editorFile.isLoading && (
            <p className="text-muted-foreground text-sm">Loading document...</p>
          )}
          {editorFile.isError && (
            <p role="alert" className="text-destructive text-sm">
              Unable to load this document. Please try again.
            </p>
          )}
          <PdfEditor
            key={`pdf-editor-${sourceFile.id}`}
            ref={editorRef}
            fileName={displayName}
            initialDocUrl={editorFile.url}
            fileType="firm-file"
            annotationUserName={annotationUserName}
            licenseKey={VITE_APRYSE_LICENSE_KEY}
            onReadyChange={setIsEditorReady}
            onAppliedRedactionsChange={setHasAppliedRedactions}
            isActive={open && !!editorFile.url}
            showSaveButton={false}
            showHeader
            className="min-h-0 flex-1"
          />
        </div>
        {!editorFile.isLoading &&
          !editorFile.isError &&
          !editorFile.canSave && (
            <p className="text-muted-foreground px-6 py-2 text-sm">
              {editorFile.saveUnavailableReason}
            </p>
          )}
        <SheetFooter addSlideoverStyles className="mt-auto shrink-0">
          <div className="flex grow items-center justify-between gap-x-2">
            {hasAppliedRedactions ? (
              <Typography className="text-muted-foreground text-sm">
                Redactions were applied. Use save as to create a new file.
              </Typography>
            ) : (
              <div />
            )}
            <div className="flex justify-end gap-x-2">
              <FormCancelButton setOpen={setOpen} isDirty />
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  void handleSaveOriginal();
                }}
                disabled={
                  !editorFile.canSave ||
                  !isEditorReady ||
                  isSaving ||
                  hasAppliedRedactions
                }
              >
                {saveMode === "original" && isSaving ? "Saving..." : "Save"}
              </Button>
              <Button
                type="button"
                variant="light-primary"
                onClick={() => {
                  void handleCreateFromPdf();
                }}
                disabled={!isEditorReady || isSaving}
              >
                {saveMode === "copy" && isSaving ? "Saving..." : "Save as"}
              </Button>
            </div>
          </div>
        </SheetFooter>
      </div>
      <ActionConfirmationModal
        open={confirmClose}
        setOpen={setConfirmClose}
        title="Unsaved changes"
        buttonText="Confirm"
        description={SLIDEOVER_CONFIRM_CLOSE_DESCRIPTION}
        afterActionFn={() => {
          setOpen(false);
          setConfirmClose(false);
        }}
      />
    </AppModal>
  );
};
